/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import { Component, OnInit, Input } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { XdaStringsService } from '../../services/xda-strings.service';
import { XdaTranslate } from '../../pipes/xda-translate.pipe';
import { DomainsModalComponent } from './domains-modal.component';
import { HttpClient } from '@angular/common/http';
import {
  ActiveDocType,
  ActiveDocumentsService,
  OpenRecord,
  Recipient,
} from '../../services/active-documents.service';
import { ErrorService } from '../../services/error.service';
import { pageSizeType } from '../controls/xrx-pagination.component';

@Component({
  selector: 'app-active-documents-modal',
  templateUrl: './active-documents-modal.component.html',
  styleUrls: ['./active-documents-modal.component.less'],
  standalone: false,
})
export class ActiveDocumentsModalComponent implements OnInit {
  @Input() doc: ActiveDocType;

  docName = '';
  docSecurityLevel = '';
  documentExpiryWarningTime = 30;
  authorizedUserList: AuthorizedUser[] = [];
  totalViewTime = '0:00:00';
  direction = 'asc';
  column = '';
  type = 'string';
  displayPagination = true;
  page = 1;
  initialPageSize = 5;
  pageSize = 5;
  pageSizeList: pageSizeType[] = [
    { size: 5, key: '', value: '' },
    { size: 10, key: '', value: '' },
  ];

  private xdaTranslatePipe: XdaTranslate;
  constructor(
    private modalService: NgbModal,
    public activeModal: NgbActiveModal,
    private xdaStringsService: XdaStringsService,
    private http: HttpClient,
    private activeDocService: ActiveDocumentsService,
    private errorService: ErrorService,
  ) {
    this.xdaTranslatePipe = new XdaTranslate(this.xdaStringsService);
  }

  ngOnInit(): void {
    let d = this.doc,
      recipients = d.documentOptions.recipients,
      totalViewTimeSeconds = 0;

    // add additional document values needed for UI
    d.documentStatus = this.activeDocService.mapDocumentStatus(
      d.documentOptions.expiryTime,
    );
    d.documentExpiryDateNgbDateStruct =
      this.activeDocService.convertToNgbDateStruct(
        d.documentOptions.expiryTime,
      );
    d.documentExpiresSoon = this.activeDocService.isDocumentExpiringSoon(
      d,
      this.documentExpiryWarningTime,
    );
    d.documentMaxDateNgbDateStruct = this.getCalendarExtremes('max');
    d.documentMinDateNgbDateStruct = this.getCalendarExtremes('min');
    d.documentSecurityLevel = this.activeDocService.mapSecurityLevel(
      d.documentOptions.distRestriction,
      d.documentOptions.readerEmail,
    );
    d.documentDomainList = this.getDomainList(d.documentOptions.domains);
    d.documentNumberOfViews = this.getNumberOfViews(d.openRecords);
    d.respondTo = this.getRespondTo(d.documentOptions.allowResponsesTo);

    recipients.forEach((recipient: Recipient) => {
      let authorizedUser: AuthorizedUser = {
          id: '',
          user: '',
          views: 0,
          enabled: false,
          viewTime: '',
          viewTimeSeconds: 0,
          firstView: 0,
          firstViewDate: '',
          firstViewTime: '',
        },
        views = d.openRecords.filter(
          (record: OpenRecord) =>
            recipient.email.toLowerCase() === record.email?.toLowerCase(),
        ),
        userTotalViewTimeSeconds = 0;

      authorizedUser.id = recipient.id;
      authorizedUser.user = recipient.email;
      authorizedUser.views = views.length;
      authorizedUser.enabled = recipient.enabled;
      views.forEach((view: OpenRecord) => {
        if (view.activityTime) {
          userTotalViewTimeSeconds += parseInt(String(view.activityTime));
        }
      });

      totalViewTimeSeconds = totalViewTimeSeconds + userTotalViewTimeSeconds;
      authorizedUser.viewTime = this.formatViewTime(userTotalViewTimeSeconds);
      authorizedUser.viewTimeSeconds = userTotalViewTimeSeconds;

      if (views.length > 0) {
        authorizedUser.firstView = new Date(views[0].opened).getTime();
        authorizedUser.firstViewDate = this.activeDocService.formatDate(
          views[0].opened,
        );
        authorizedUser.firstViewTime = this.activeDocService.formatTime(
          views[0].opened,
        );
      }

      this.authorizedUserList.push(authorizedUser);
    });

    // Include activity time for viewers not in recipient list.
    const unknownViewers = d.openRecords.filter(
      (record: OpenRecord) =>
        !recipients.some(
          (recipient: Recipient) =>
            recipient.email.toLowerCase() === record.email?.toLowerCase(),
        ),
    );
    unknownViewers.forEach((view: OpenRecord) => {
      if (view.activityTime)
        totalViewTimeSeconds += parseInt(String(view.activityTime));
    });

    this.totalViewTime = this.formatViewTime(totalViewTimeSeconds);

    if (this.authorizedUserList.length <= this.pageSize) {
      this.displayPagination = false;
    }
  }

  formatViewTime(secondsInput: number): string {
    const hours = Math.floor(secondsInput / 3600),
      minutes = Math.floor((secondsInput - hours * 3600) / 60),
      seconds = secondsInput - hours * 3600 - minutes * 60;

    const pad = function (num: number) {
      if (num < 10) {
        return `0${num}`;
      } else {
        return num;
      }
    };
    return hours + ':' + pad(minutes) + ':' + pad(seconds);
  }

  getCalendarExtremes(type: string): NgbDateStruct {
    let date = new Date(),
      newDate;
    switch (type) {
      case 'max':
        const createDate = new Date(this.doc.created);
        newDate = createDate.setFullYear(createDate.getFullYear() + 1);
        break;
      case 'min':
        newDate = date.setDate(date.getDate() + 1);
        break;
      default:
        newDate = new Date().getTime();
    }

    return this.activeDocService.convertToNgbDateStruct(String(newDate));
  }

  getDomainList(domains: string): string[] {
    let domainArr: string[] = [];
    if (domains && domains.length) {
      domainArr = domains.split(',');
      domainArr = domainArr.map((d) => d.trim());
    }

    return domainArr;
  }

  getNumberOfViews(openRecords: OpenRecord[]): number {
    if (openRecords && openRecords.length) {
      return openRecords.length;
    }
    return 0;
  }

  getRespondTo(value: any) {
    if (value == null || value == '') {
      return 'SDE_EMAIL_NOT_PROVIDED';
    } else {
      return value;
    }
  }

  viewDomains(event: Event) {
    event.preventDefault();
    const activeDocsModalRef = this.modalService.open(DomainsModalComponent, {
      windowClass: 'xrx-modal modal-sm',
    });
    activeDocsModalRef.componentInstance.domains =
      this.doc.documentOptions.domains;
    activeDocsModalRef.result.then(
      (data: string) => {},
      () => {},
    );
  }

  updateActiveDoc() {
    // update RPost enabled status based on expiration
    this.doc.documentOptions.enabled = this.activeDocService.mapDocumentEnabled(
      this.doc.documentOptions.expiryTime,
    );

    const formData = new FormData();
    formData.append('document', JSON.stringify(this.doc));
    this.http
      .put(
        `${this.activeDocService.getRPostBaseUrl()}docs/${this.doc.id}`,
        formData,
      )
      .toPromise()
      .then((result) => {
        this.activeModal.close(this.doc);
      })
      .catch((error) => {
        console.log(error);
        // probably want a failed update error here?
        this.errorService.displayGeneralError().result.then(() => {
          this.activeModal.close(this.doc);
        });
      });
  }

  onToggle(authorizedUser: AuthorizedUser) {
    authorizedUser.enabled = !authorizedUser.enabled;

    const recipient = this.doc.documentOptions.recipients.find(
      (x: Recipient) => x.id == authorizedUser.id,
    );
    if (recipient) {
      recipient.enabled = !recipient.enabled;
    }
  }

  onExpirationChange(date: any) {
    this.doc.documentOptions.expiryTime = new Date(
      date.year,
      date.month - 1,
      date.day,
      23,
      59,
    ).toISOString();
  }

  expireNow() {
    const now: Date = new Date();
    this.doc.documentOptions.expiryTime = now.toISOString();
    setTimeout(() => {
      this.doc.documentExpiryDateNgbDateStruct = {
        year: now.getFullYear(),
        month: now.getMonth() + 1,
        day: now.getDate(),
      };
    }, 1);
  }

  setSortParams(param: any) {
    this.direction = param.direction;
    this.column = param.column;
    this.type = param.type;
  }
}

interface AuthorizedUser {
  id: string;
  user: string;
  views: number;
  enabled: boolean;
  viewTime: string;
  viewTimeSeconds: number;
  firstView: number;
  firstViewDate: string;
  firstViewTime: string;
}
